import React, {PropsWithChildren, useState} from 'react';
import {Form, FormItem, Input, Select} from 'formik-antd';
import {Collapse} from 'antd';
import styled from 'styled-components';
import {FormProps} from 'antd/es/form';
import {PhoneNumberInput} from 'components/Form/PhoneNumberInput';
import {UrlInput} from 'components/Form/UrlInput';
import {useCountries} from 'hooks/useCountries';
import {IBANInput} from 'components';
import * as yup from 'yup';
import * as iban from 'ibantools';
import {CreateStoreInput} from 'api/generated';
import {useFormikContext} from 'formik';

const Panel = styled(Collapse.Panel)`
  margin: 0 -16px;
`;

const {Option} = Select;

export const validationSchema = yup.object().shape({
  name: yup.string().required('Name is required').typeError('Name must be a string'),
  email: yup.string().email('Invalid email').typeError('Email must be a string').nullable(),
  phone: yup
    .string()
    .matches(/^\+?\d{1,15}$/, 'Phone number is not valid')
    .typeError('Phone number must be a string')
    .nullable(),
  iban: yup
    .string()
    .test('iban', 'Invalid IBAN', (value) => !value || iban.isValidIBAN(value!))
    .nullable()
});

export const StoreForm: React.FC<PropsWithChildren<FormProps & {labels?: boolean}>> = (props) => {
  const {getIsoCountry, getCountryName, isoCountries, loading} = useCountries();
  const [countryCode, setCountryCode] = useState();
  const selectedCountry = getIsoCountry(countryCode);
  const {setFieldValue} = useFormikContext<CreateStoreInput>();

  return (
    <Form preserve={false} {...props}>
      <FormItem name="name" label="Name">
        <Input name="name" placeholder="Store name" />
      </FormItem>
      <FormItem name="email" label="E-mail">
        <Input name="email" placeholder="Store e-mail" />
      </FormItem>
      <FormItem name="phone" label="Phone number">
        <PhoneNumberInput name="phone" placeholder="Store phone number" allowClear allowEmpty />
      </FormItem>
      <FormItem name="website" label="Website">
        <UrlInput name="website" placeholder="mycompany.com" />
      </FormItem>
      <FormItem name="iban" label="IBAN">
        <IBANInput name="iban" placeholder="IBAN" />
      </FormItem>
      <Collapse ghost defaultActiveKey="Address">
        <Panel header="Address" key="Address">
          <FormItem name="address.country" label="Address">
            <Select
              name="address.country"
              placeholder="Country"
              optionFilterProp="children"
              showSearch
              loading={loading}
              onChange={(value) => {
                setFieldValue(`address.country`, value);
                setFieldValue(`address.state`, '');
                setFieldValue(`address.zip`, '');
                setCountryCode(value);
              }}>
              {isoCountries.map((country) => (
                <Option key={country.code} value={country.code}>
                  {getCountryName(country.code)}
                </Option>
              ))}
            </Select>
          </FormItem>
          <FormItem name="address.state" label="State / Province / Region">
            {selectedCountry?.divisions ? (
              <Select
                name="address.state"
                optionFilterProp="children"
                showSearch
                placeholder="State / Province / Region">
                {selectedCountry &&
                  Object.keys(selectedCountry.divisions)
                    .sort((code1, code2) =>
                      selectedCountry.divisions[code1].localeCompare(
                        selectedCountry.divisions[code2]
                      )
                    )
                    .map((code) => (
                      <Option
                        key={selectedCountry.divisions[code]}
                        value={selectedCountry.divisions[code]}>
                        {selectedCountry.divisions[code]}
                      </Option>
                    ))}
              </Select>
            ) : (
              <Input name="address.state" placeholder="State / Province / Region" />
            )}
          </FormItem>
          <FormItem name="address.city" label="City">
            <Input name="address.city" placeholder="City" />
          </FormItem>
          <FormItem name="address.line1" label="Street, 123">
            <Input name="address.line1" placeholder="Street, 123" />
          </FormItem>
          <FormItem name="address.zip" label="Zip / Postal code">
            <Input name="address.zip" placeholder="Zip / Postal code" />
          </FormItem>
        </Panel>
      </Collapse>
      {props.children}
    </Form>
  );
};
